import React from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Brush,
  Legend
} from 'recharts';
import './PriceChart.css';

interface PriceData {
  date: string;
  price: number;
  volume?: number;
}

interface PriceChartProps {
  data: PriceData[];
  title: string;
  loading?: boolean;
}

const PriceChart: React.FC<PriceChartProps> = ({ data, title, loading }) => {
  if (loading) {
    return (
      <div className="price-chart-container">
        <div className="chart-loading">
          <div className="loading-spinner"></div>
          <p>Loading price history...</p>
        </div>
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className="price-chart-container">
        <div className="chart-empty">
          <p>No price history available for {title}</p>
        </div>
      </div>
    );
  }

  // Format data for chart
  const chartData = data.map(item => ({
    ...item,
    date: new Date(item.date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric'
    }),
    fullDate: item.date
  }));

  // Custom tooltip
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="chart-tooltip">
          <p className="tooltip-date">{new Date(data.fullDate).toLocaleDateString()}</p>
          <p className="tooltip-price">
            <span className="tooltip-label">Price:</span>
            <span className="tooltip-value">${payload[0].value.toLocaleString()}</span>
          </p>
          {data.volume && (
            <p className="tooltip-volume">
              <span className="tooltip-label">Volume:</span>
              <span className="tooltip-value">{data.volume} sold</span>
            </p>
          )}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="price-chart-container">
      <div className="chart-header">
        <h3>📈 Price History: {title}</h3>
        <p>Showing {data.length} data points over time</p>
      </div>

      <div className="chart-wrapper">
        <ResponsiveContainer width="100%" height={400}>
          <LineChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
            <XAxis
              dataKey="date"
              stroke="#b0b0b0"
              fontSize={12}
              tick={{ fill: '#b0b0b0' }}
            />
            <YAxis
              stroke="#b0b0b0"
              fontSize={12}
              tick={{ fill: '#b0b0b0' }}
              tickFormatter={(value) => `$${value.toLocaleString()}`}
            />
            <Tooltip content={<CustomTooltip />} />
            <Line
              type="monotone"
              dataKey="price"
              stroke="#667eea"
              strokeWidth={3}
              dot={{ fill: '#667eea', strokeWidth: 2, r: 4 }}
              activeDot={{ r: 6, stroke: '#667eea', strokeWidth: 2 }}
            />
            <Brush
              dataKey="date"
              height={30}
              stroke="#667eea"
              fill="rgba(102, 126, 234, 0.1)"
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default PriceChart;
